import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mdi/react';
import { mdiMicroscope, mdiClipboardTextOutline } from '@mdi/js';
import { FaCheck } from 'react-icons/fa6';
import { NON_TOBACCO } from '../../../constants/App';

const TABLE_RATINGS = ['Table B', 'Table C', 'Table D', 'Table E', 'Table F', 'Table H'];

/**
 * LifeDemographicsSection Component
 * Demographic fields for life insurance quotes in the Proposed Insured column
 */
const LifeDemographicsSection = ({
  formData,
  onInputChange,
  hasHealthAnalyzer,
  handleHealthAnalyzerClick,
  onHandleHealthClassSelect,
  underwritingClasses,
  setShowUwClassesModal,
  isNonEmpty,
  styles
}) => {
  const toggleItem = (arr, item) => (arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);

  return (
    <>
      {/* Date of Birth */}
      <div style={styles.formGroup} data-itemid='dob-wrapper'>
        <div style={styles.row} data-itemid='dob-label-wrapper'>
          <label style={styles.label} data-itemid='dob-label'>
            Date of Birth
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='dob-label-required'>
            *
          </label>
        </div>
        <input
          data-itemid='dob-field'
          onBlur={e => {
            if (!e.target.value) e.target.type = 'text';
          }}
          onChange={e => onInputChange('dateOfBirthLife', e.target.value)}
          onFocus={e => (e.target.type = 'date')}
          placeholder='Select Date'
          style={styles.input}
          type='text'
          value={formData.dateOfBirthLife || ''}
        />
      </div>

      {/* Gender */}
      <div style={styles.formGroup} data-itemid='gender-wrapper'>
        <div style={styles.row} data-itemid='gender-label-wrapper'>
          <label style={styles.label} data-itemid='gender-label'>
            Gender
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='gender-label-required'>
            *
          </label>
        </div>
        <div style={styles.buttonGroup} data-itemid='gender-options-wrapper'>
          {['Male', 'Female'].map(gender => (
            <button key={gender} data-itemid={`gender-${gender}`} onClick={() => onInputChange('genderLife', gender)} style={styles.button(formData.genderLife === gender)} type='button'>
              {gender}
              {formData.genderLife === gender && <FaCheck style={{ marginLeft: '4px', fontSize: '12px' }} />}
            </button>
          ))}
        </div>
      </div>

      {/* Tobacco Status */}
      <div style={styles.formGroup} data-itemid='tobacco-status-wrapper'>
        <div style={styles.row} data-itemid='tobacco-status-label-wrapper'>
          <label style={styles.label} data-itemid='tobacco-status-label'>
            Tobacco Status
          </label>
          <label style={{ ...styles.label, color: 'red' }} data-itemid='tobacco-status-label-required'>
            *
          </label>
        </div>
        <div style={styles.buttonGroup} data-itemid='tobacco-status-options-wrapper'>
          {['Non-Tobacco', 'Tobacco'].map(status => (
            <button
              key={status}
              data-itemid={`tobacco-status-button-${status}`}
              onClick={() => onInputChange('tobacco', status === 'Tobacco')}
              style={styles.button(formData.tobacco === (status === 'Tobacco'))}
              type='button'
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Health Class */}
      <div style={styles.formGroup} data-itemid='health-class-wrapper'>
        <label style={styles.label} data-itemid='health-class-label'>
          Health Class{' '}
          {hasHealthAnalyzer && (
            <button
              onClick={handleHealthAnalyzerClick}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: '#017DB7',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              type='button'
              data-itemid='health-analyzer-button'
            >
              <Icon path={mdiMicroscope} size={0.6} data-itemid='health-analyzer-icon' /> Health Analyzer
            </button>
          )}
        </label>
        <div style={styles.buttonGroup} data-itemid='health-class-options-wrapper'>
          {[...NON_TOBACCO, 'Table Rated'].map(health => (
            <button
              key={health}
              data-itemid={`"health-class-options-button-${health}`}
              onClick={() => onHandleHealthClassSelect(formData, health)}
              style={styles.button(health === 'Table Rated' ? !!formData.showTableRatings : formData.healthClass?.includes(health))}
              type='button'
            >
              {health}
            </button>
          ))}
        </div>

        {/* Table Ratings */}
        {formData?.showTableRatings && (
          <div style={{ marginTop: 8, border: '1px solid #dee2e6', borderRadius: 4, padding: '8px', backgroundColor: '#f8f9fa' }} data-itemid='table-rated-conditional-options-wrapper'>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#495057' }}>Select Table Ratings:</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {TABLE_RATINGS.map(tableRating => (
                <button
                  key={tableRating}
                  data-itemid={`table-rated-conditional-options-button-${tableRating}`}
                  onClick={() => {
                    const current = formData.healthClass || [];
                    if (!formData.showTableRatings) {
                      onInputChange('showTableRatings', true);
                    }
                    onInputChange('healthClass', toggleItem(current, tableRating));
                  }}
                  style={{
                    ...styles.button((formData.healthClass || []).includes(tableRating)),
                    fontSize: 11,
                    padding: '4px 8px'
                  }}
                  type='button'
                >
                  {tableRating.replace('Table ', '')}
                  {(formData.healthClass || []).includes(tableRating) ? <FaCheck /> : ''}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Underwriting Classes */}
      {hasHealthAnalyzer && isNonEmpty(underwritingClasses) && (
        <div style={styles.formGroup} data-itemid='underwriting-classes-wrapper'>
          <label style={styles.label} data-itemid='underwriting-classes-label'>
            Underwriting Classes
          </label>
          <button
            data-itemid='underwriting-classes-button'
            onClick={() => setShowUwClassesModal(true)}
            style={{
              ...styles.input,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              fontWeight: '500'
            }}
            type='button'
          >
            <Icon path={mdiClipboardTextOutline} size={0.7} data-itemid='underwriting-classes-wrapper' />
            View Results ({underwritingClasses.length})
          </button>
        </div>
      )}
    </>
  );
};

LifeDemographicsSection.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  hasHealthAnalyzer: PropTypes.bool,
  handleHealthAnalyzerClick: PropTypes.func,
  onHandleHealthClassSelect: PropTypes.func.isRequired,
  underwritingClasses: PropTypes.array,
  setShowUwClassesModal: PropTypes.func,
  isNonEmpty: PropTypes.func,
  styles: PropTypes.object.isRequired
};

export default LifeDemographicsSection;
